import { GridCol } from '@/components/nsw/grid/GridCol'
import { GridWrapper } from '@/components/nsw/grid/GridWrapper'
import { STYLES } from '@/constants/styles'
import { TREE_ID_LABELS } from '@/constants/treepickeroptions'
import { Syllabus } from '@/kontent/content-types'
import { TaxoKeyLearningArea } from '@/kontent/taxonomies'
import { CustomViewErrorAlert } from '@/layouts/wp_custom_view'
import { TaxoStageWithLifeSkill } from '@/types'
import {
	byTaxoCodename,
	isAllowPreviewExternalSyllabus,
	isIntersect,
	isYes,
} from '@/utils'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import { ElementModels } from '@kontent-ai/delivery-sdk'
import { forwardRef, useState } from 'react'
import { arrayToggleMultiple } from '../../utilities/functions'
import TreePicker from './TreePicker'
import { TreeElement } from './treeUtils'

export const makeLearningAreaTree = (
	syllabuses: Syllabus[],
	keyLearningAreas: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[],
	isSyllabusValueFromSyllabusTaxoCodename = false,
	disabledKeyLearningAreas = [] as TaxoKeyLearningArea[],
	withPublicationYear = true,
): TreeElement[] =>
	keyLearningAreas.map((kla) => {
		const children = syllabuses
			.filter(
				(syl) =>
					syl.elements.key_learning_area__items.value.some(
						(sylKla) => sylKla.codename === kla.codename,
					) &&
					(isAllowPreviewExternalSyllabus() ||
						!isYes(syl.elements.doredirect)),
			)
			.map((syl) => {
				const syllabusTitleWithPublicationYear =
					getSyllabusTitleWithPublicationYear(syl, false)

				return {
					id: isSyllabusValueFromSyllabusTaxoCodename
						? syl.elements.syllabus.value?.[0]?.codename
						: syl.system.codename,
					label: withPublicationYear
						? syllabusTitleWithPublicationYear
						: syl.elements.title.value,
					disabled: isIntersect(
						syl.elements.key_learning_area__items.value.map(
							byTaxoCodename,
						),
						disabledKeyLearningAreas,
					),
				}
			})
		const childrenNotDisabled = children.filter((c) => !c.disabled)

		return {
			id: kla.codename,
			label: kla.name,
			disabled: !childrenNotDisabled.length,
			children: children,
		}
	})

export const makeStageOptions = (
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[],
	disabledStages: TaxoStageWithLifeSkill[] = [],
): TreeElement[] => {
	const _temp = stages.map((s) => ({
		id: s.codename,
		label: s.name,
		disabled: disabledStages.includes(s.codename),
	}))
	return _temp
}

export const makeNestedStageGroups = (
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[],
	disabledStages: TaxoStageWithLifeSkill[] = [],
	customLifeSkillsLabel?: string,
	hideLifeSkillsDivider = false,
): TreeElement[] => {
	const primaryStages = stages
		.filter((stage) =>
			['early_stage_1', 'stage_1', 'stage_2', 'stage_3'].includes(
				stage.codename,
			),
		)
		.map((s) => ({
			id: s.codename,
			label: s.name,
			disabled: disabledStages.includes(s.codename),
		}))

	// Secondary stages (Stages 4-5 correspond to years 7-10)
	const secondaryStagesBase = stages
		.filter((stage) => ['stage_4', 'stage_5'].includes(stage.codename))
		.map((s) => ({
			id: s.codename,
			label: s.name,
			disabled: disabledStages.includes(s.codename),
		}))

	// Add Life Skills to secondary group with custom label and divider control
	const lifeSkillsStage = stages.find(
		(stage) => stage.codename === 'life_skills',
	)
	const secondaryStages = [...secondaryStagesBase]
	if (lifeSkillsStage) {
		secondaryStages.push({
			id: lifeSkillsStage.codename,
			label: customLifeSkillsLabel || lifeSkillsStage.name,
			disabled: disabledStages.includes(lifeSkillsStage.codename),
		})
	}

	// Senior (11-12) - hoist as direct option instead of nested group
	const stage6 = stages.find((stage) => stage.codename === 'stage_6')

	const stageGroups: TreeElement[] = []

	if (primaryStages.length > 0) {
		const allPrimaryDisabled = primaryStages.every(
			(stage) => stage.disabled,
		)
		stageGroups.push({
			id: 'primary-group',
			label: 'Primary (K–6)',
			children: primaryStages,
			disabled: allPrimaryDisabled,
		})
	}

	if (secondaryStages.length > 0) {
		const allSecondaryDisabled = secondaryStages.every(
			(stage) => stage.disabled,
		)
		stageGroups.push({
			id: 'secondary-group',
			label: 'Secondary (7–10)',
			children: secondaryStages,
			disabled: allSecondaryDisabled,
		})
	}

	// Hoist Senior (11-12) as direct option
	if (stage6) {
		stageGroups.push({
			id: stage6.codename,
			label: 'Senior (11–12)',
			disabled: disabledStages.includes(stage6.codename),
		})
	}

	return stageGroups
}

export interface BaseSyllabusPickerProps {
	syllabuses: Syllabus[]
	keyLearningAreas: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[]
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[]
	disabledLearningAreas: TaxoKeyLearningArea[]
	disabledStages: TaxoStageWithLifeSkill[]
	syllabusElementsOptions: TreeElement[]
	hideLearningAreasColumn?: boolean
	useNestedStageGroups?: boolean
	showElementsSelectAll?: boolean
	hideElementsExpandCollapse?: boolean
	showAllExpandables?: boolean
	customLifeSkillsLabel?: string
	hideLifeSkillsDivider?: boolean
	hideAllDividers?: boolean
}

export interface CustomSyllabusPickerProps extends BaseSyllabusPickerProps {
	showLearningAreaError: boolean
	showStageError: boolean
	showElementsError: boolean
	onChange: (_selected: {
		learningAreas: string[]
		stages: string[]
		elements: string[]
	}) => void

	initialSelectedSyllabuses?: string[]
	initialSelectedStages?: string[]
	initialSelectedElements?: string[]
	hideLearningAreasColumn?: boolean
	disableAutoContentSelection?: boolean
}

const CustomSyllabusPicker = forwardRef<
	HTMLDivElement,
	CustomSyllabusPickerProps
>((props, ref) => {
	const {
		onChange,
		syllabuses: allSyllabuses,
		keyLearningAreas,
		stages: allStages,
		showLearningAreaError,
		showStageError,
		showElementsError,
		disabledLearningAreas,
		disabledStages,
		syllabusElementsOptions,
		initialSelectedSyllabuses = [],
		initialSelectedStages = [],
		initialSelectedElements = [],
		hideLearningAreasColumn = false,
		useNestedStageGroups = false,
		showElementsSelectAll = false,
		showAllExpandables = false,
		customLifeSkillsLabel,
		hideLifeSkillsDivider = false,
		hideAllDividers = false,
		disableAutoContentSelection = false,
	} = props

	// selected items
	const [learningAreas, setLearningAreas] = useState<string[]>(
		initialSelectedSyllabuses,
	)
	const [stages, setStages] = useState<string[]>(initialSelectedStages)
	const [elements, setElements] = useState<string[]>(initialSelectedElements)

	const handleLearningAreas = (ids: string[]) => {
		const updated = arrayToggleMultiple(learningAreas, ids)
		setLearningAreas(updated)

		if (onChange) {
			onChange({
				learningAreas: updated,
				stages,
				elements,
			})
		}
	}

	const handleStages = (ids: string[]) => {
		const updated = arrayToggleMultiple(stages, ids)
		setStages(updated)

		if (onChange) {
			onChange({
				learningAreas,
				stages: updated,
				elements,
			})
		}
	}

	const handleElements = (ids: string[]) => {
		let updated = arrayToggleMultiple(elements, ids)

		// Skip auto content selection logic if disabled
		if (!disableAutoContentSelection) {
			// DC-357 Clear AccessPoints and Examples when content is unselected
			if (
				ids.includes(TREE_ID_LABELS.CONTENT.id) &&
				!updated.includes(TREE_ID_LABELS.CONTENT.id)
			) {
				updated = updated.filter(
					(element) =>
						//element !== TREE_ID_LABELS.ADDRESSED_IN_PARALLEL.id &&
						element !== TREE_ID_LABELS.ACCESS_POINTS.id &&
						element !== TREE_ID_LABELS.EXAMPLES.id &&
						element !== TREE_ID_LABELS.CURRICULUM_CONNECTIONS.id &&
						element !== TREE_ID_LABELS.TAGS.id,
				)
			}
			// DC-357 Select content if AccessPoints or Examples are selected
			if (
				//updated.includes(TREE_ID_LABELS.ADDRESSED_IN_PARALLEL.id) ||
				(updated.includes(TREE_ID_LABELS.ACCESS_POINTS.id) ||
					updated.includes(TREE_ID_LABELS.EXAMPLES.id) ||
					updated.includes(
						TREE_ID_LABELS.CURRICULUM_CONNECTIONS.id,
					) ||
					updated.includes(TREE_ID_LABELS.TAGS.id)) &&
				!updated.includes(TREE_ID_LABELS.CONTENT.id)
			) {
				updated.push(TREE_ID_LABELS.CONTENT.id)
			}
		}

		setElements(updated)

		if (onChange) {
			onChange({
				learningAreas,
				stages,
				elements: updated,
			})
		}
	}

	const createElementsWithSelectAll = (
		syllabusElementsOptions: TreeElement[],
	): TreeElement[] => {
		if (!showElementsSelectAll) {
			return syllabusElementsOptions
		}

		return [
			{
				id: 'select-all',
				label: 'Select all',
				children: syllabusElementsOptions,
			},
		]
	}

	const shortElementsIds = [
		'course-overview',
		'rationale',
		'aim',
		'outcomes',
		'content',
		'access-points',
	]
	const additionalContentElementsIds = [
		'examples',
		'tags',
		'teaching-advice',
		'assessment',
		'glossary',
	]
	const createShortElementsWithSelectAll = (
		syllabusElementsOptions: TreeElement[],
	): TreeElement[] => {
		const shortElementsOptions = syllabusElementsOptions.filter((e) =>
			shortElementsIds.includes(e.id),
		)
		if (!showElementsSelectAll) {
			return shortElementsOptions
		}

		return [
			{
				id: 'select-all',
				label: 'Select all',
				children: shortElementsOptions,
			},
		]
	}
	const createAdditionalContentElementsWithSelectAll = (
		syllabusElementsOptions: TreeElement[],
	): TreeElement[] => {
		const additionalContentElementsOptions = syllabusElementsOptions.filter(
			(e) => additionalContentElementsIds.includes(e.id),
		)
		if (!showElementsSelectAll) {
			return additionalContentElementsOptions
		}

		return [
			{
				id: 'select-all',
				label: 'Select all',
				children: additionalContentElementsOptions,
			},
		]
	}

	const showCurrentElementsError = (showElementsError: boolean) => {
		if (!hideLearningAreasColumn) {
			return showElementsError
		} else {
			const shortElements =
				elements && elements.length > 0
					? elements.filter((e) => shortElementsIds.includes(e))
					: []
			return shortElements.length === 0
		}
	}

	return (
		<div ref={ref}>
			<GridWrapper>
				{!hideLearningAreasColumn && (
					<GridCol lg={4}>
						<TreePicker
							title="Learning areas and subjects *"
							rootElements={makeLearningAreaTree(
								allSyllabuses,
								keyLearningAreas,
								false,
								disabledLearningAreas,
							)}
							selected={learningAreas}
							onChange={handleLearningAreas}
							slotAfterTitle={
								showLearningAreaError && (
									<CustomViewErrorAlert>
										Please select a learning area
									</CustomViewErrorAlert>
								)
							}
							defaultExpanded={
								showAllExpandables ? 'all' : 'none'
							}
							hideExpandToggle={showAllExpandables}
							hideAllDividers={hideAllDividers}
						/>
					</GridCol>
				)}
				<GridCol lg={4}>
					<TreePicker
						title="Stages *"
						rootElements={
							useNestedStageGroups
								? makeNestedStageGroups(
										allStages,
										disabledStages,
										customLifeSkillsLabel,
										hideLifeSkillsDivider,
								  )
								: makeStageOptions(allStages, disabledStages)
						}
						selected={stages}
						onChange={handleStages}
						slotAfterTitle={
							showStageError && (
								<CustomViewErrorAlert>
									Please select a stage
								</CustomViewErrorAlert>
							)
						}
						css={
							hideAllDividers
								? {
										'.tree-picker-id-life_skills': {
											borderTop: 'none',
											marginTop: 0,
											paddingTop: 0,
										},
								  }
								: STYLES.LIFE_SKILL_TREE_PICKER
						}
						defaultExpanded={showAllExpandables ? 'all' : 'none'}
						hideExpandToggle={showAllExpandables}
						hideAllDividers={hideAllDividers}
					/>
				</GridCol>
				<GridCol lg={4}>
					<TreePicker
						title="Syllabus elements *"
						rootElements={
							hideLearningAreasColumn
								? createShortElementsWithSelectAll(
										syllabusElementsOptions,
								  )
								: createElementsWithSelectAll(
										syllabusElementsOptions,
								  )
						}
						selected={elements}
						onChange={handleElements}
						slotAfterTitle={
							showCurrentElementsError(showElementsError) && (
								<CustomViewErrorAlert>
									Please select a syllabus element
								</CustomViewErrorAlert>
							)
						}
						css={
							hideAllDividers
								? {
										[`.tree-picker-id-${TREE_ID_LABELS.TAGS.id}`]:
											{
												borderTop: 'none',
												marginTop: 0,
												paddingTop: 0,
											},
										[`.tree-picker-id-${TREE_ID_LABELS.ACCESS_POINTS.id}`]:
											{
												marginLeft: '2rem',
											},
								  }
								: STYLES.TAGS_TREE_PICKER
						}
						defaultExpanded={showAllExpandables ? 'all' : 'none'}
						hideExpandToggle={showAllExpandables}
						hideAllDividers={hideAllDividers}
					/>
				</GridCol>
				{hideLearningAreasColumn && (
					<GridCol lg={4}>
						<TreePicker
							title="Additional"
							rootElements={createAdditionalContentElementsWithSelectAll(
								syllabusElementsOptions,
							)}
							selected={elements}
							onChange={handleElements}
							css={
								hideAllDividers
									? {
											[`.tree-picker-id-${TREE_ID_LABELS.TAGS.id}`]:
												{
													borderTop: 'none',
													marginTop: 0,
													paddingTop: 0,
												},
									  }
									: STYLES.TAGS_TREE_PICKER
							}
							defaultExpanded={
								showAllExpandables ? 'all' : 'none'
							}
							hideExpandToggle={showAllExpandables}
							hideAllDividers={hideAllDividers}
						/>
					</GridCol>
				)}
			</GridWrapper>
		</div>
	)
})

export default CustomSyllabusPicker
